<template>
  <div id="cadastro-usuario">
    <div class="cadastro-container">
      <el-card class="box-card">
        <h2 class="title">
          {{ i18n.cadastro.titulos.cadastroUsuario }}
        </h2>

        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-position="top"
          label-width="auto"
        >
          <div class="toggle-tipo">
            <el-radio-group
              v-model="form.pessoa.estrangeiro"
              @change="onEstrangeiroChange"
            >
              <el-radio :label="false">Brasileiro</el-radio>
              <el-radio :label="true">Estrangeiro (Foreigner)</el-radio>
            </el-radio-group>
          </div>

          <DadosPessoaisForm
            :value="form.pessoa"
            :estrangeiro="form.pessoa.estrangeiro"
            :paises="paises"
            @validate-field="validateField"
          />

          <div class="section-title">
            <h3>{{ i18n.cadastro.titulos.seguranca }}</h3>
          </div>

          <el-row :gutter="16">
            <el-col :xs="24" :sm="12">
              <el-form-item :label="i18n.cadastro.labels.senha" prop="senha">
                <el-input
                  type="password"
                  v-model="form.senha"
                  autocomplete="new-password"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item
                :label="i18n.cadastro.labels.confirmarSenha"
                prop="confirmarSenha"
              >
                <el-input
                  type="password"
                  v-model="form.confirmarSenha"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <EnderecoForm
            :value="form.pessoa"
            :estrangeiro="form.pessoa.estrangeiro"
            :paises="paises"
            @validate-field="validateField"
          />

          <div class="actions cadastro-usuario__actions">
            <el-button @click="$router.push('/login')" class="green-border">{{
              i18n.cadastro.botoes.cancelar
            }}</el-button>
            <el-button type="primary" @click="submitForm">{{
              i18n.cadastro.botoes.salvar
            }}</el-button>
          </div>
        </el-form>
      </el-card>
    </div>
    <confirm-modal
      :visible.sync="modalSucessoVisivel"
      :title="textosSucessoConfirmacao.titulo"
      :close-on-click-outside-or-escape="false"
      :show-close="false"
      :left-button="{ show: false }"
      :right-button="{
        show: true,
        text: textoBotaoIrLogin,
        onClick: irParaLogin
      }"
      width="480px"
      @close="irParaLogin"
    >
      <p class="modal-sucesso-descricao">
        {{ textosSucessoConfirmacao.descricao }}
      </p>
      <p class="modal-sucesso-hint">{{ textoRedirecionamento }}</p>
    </confirm-modal>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import "@/utils/helpers/formatacao";
import RegistroService from "@/services/RegistroService";
import ConsultaService from "@/services/ConsultaService";
import DadosPessoaisForm from "@/components/business/cadastro/DadosPessoaisForm";
import EnderecoForm from "@/components/business/cadastro/EnderecoForm";
import {
  PT_CADASTRO,
  EN_CADASTRO
} from "@/utils/messages/interface/registrar/identificacao/cadastro";
import {
  PT_VALIDATION,
  EN_VALIDATION
} from "@/utils/messages/validation/validation";
import { buildUsuarioRegistroPayload } from "@/utils/buildUsuarioRegistroPayload";
import { buildErrorNotifyPayload } from "@/services/shared/handling/apiErrorNotification";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";

const SEGUNDOS_REDIRECIONAMENTO_LOGIN = 5;

export default {
  name: "CadastroUsuario",
  directives: { mask },
  components: {
    DadosPessoaisForm,
    EnderecoForm,
    ConfirmModal
  },
  computed: {
    i18n() {
      return this.form.pessoa.estrangeiro ? EN_CADASTRO : PT_CADASTRO;
    },
    validationMessages() {
      return this.form.pessoa.estrangeiro ? EN_VALIDATION : PT_VALIDATION;
    },
    rules() {
      return {
        "pessoa.nome": [
          {
            required: true,
            message: this.validationMessages.validation.nome.required,
            trigger: "blur"
          }
        ],
        "pessoa.cpf": [
          {
            required: true,
            message: this.validationMessages.validation.cpf.required,
            trigger: "blur"
          },
          {
            validator: (rule, value, callback) => {
              if (value && !value.isCPF()) {
                callback(
                  new Error(this.validationMessages.validation.cpf.invalid)
                );
              } else {
                callback();
              }
            },
            trigger: "blur"
          }
        ],
        "pessoa.passaporte": [
          {
            required: true,
            message: this.validationMessages.validation.passaporte.required,
            trigger: "blur"
          }
        ],
        "pessoa.dataNascimentoString": [
          {
            required: true,
            message: this.validationMessages.validation.dataNascimento.required,
            trigger: ["blur", "change"]
          }
        ],
        "pessoa.nomeMae": [
          {
            required: true,
            message: this.validationMessages.validation.nomeMae.required,
            trigger: "blur"
          }
        ],
        "pessoa.sexo": [
          {
            required: true,
            message: this.validationMessages.validation.sexo.required,
            trigger: "change"
          }
        ],
        "pessoa.nacionalidade": [
          {
            validator: (rule, value, callback) => {
              if (!this.form.pessoa.estrangeiro) {
                callback();
                return;
              }
              if (value && value.id != null) {
                callback();
              } else {
                callback(
                  new Error(
                    this.validationMessages.validation.nacionalidade.required
                  )
                );
              }
            },
            trigger: "change"
          }
        ],
        "pessoa.email": [
          {
            required: true,
            message: this.validationMessages.validation.email.required,
            trigger: "blur"
          },
          {
            type: "email",
            message: this.validationMessages.validation.email.invalid,
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.cep": [
          {
            required: true,
            message: this.validationMessages.validation.cep.required,
            trigger: "blur"
          },
          {
            validator: (rule, value, callback) => {
              if (value) {
                const cep = value.replace(/\D/g, "");
                if (cep.length !== 8) {
                  callback(
                    new Error(this.validationMessages.validation.cep.length)
                  );
                  return;
                }
              }
              callback();
            },
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.logradouro": [
          {
            required: true,
            message: this.validationMessages.validation.logradouro.required,
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.numero": [
          {
            validator: (rule, value, callback) => {
              if (
                !this.form.pessoa.enderecoPrincipal.semNumero &&
                (value === null || value === undefined || value === "")
              ) {
                callback(
                  new Error(this.validationMessages.validation.numero.required)
                );
              } else {
                callback();
              }
            },
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.bairro": [
          {
            required: true,
            message: this.validationMessages.validation.bairro.required,
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.municipioNome": [
          {
            required: true,
            message: this.validationMessages.validation.municipio.required,
            trigger: "blur"
          }
        ],
        "pessoa.enderecoPrincipal.uf": [
          {
            required: true,
            message: this.validationMessages.validation.uf.required,
            trigger: "blur"
          }
        ],
        "pessoa.enderecoEstrangeiro.pais": [
          {
            required: true,
            message: this.validationMessages.validation.pais.required,
            trigger: "change"
          }
        ],
        "pessoa.enderecoEstrangeiro.zipCode": [
          {
            required: true,
            message: this.validationMessages.validation.cep.required,
            trigger: ["blur", "change"]
          }
        ],
        "pessoa.enderecoEstrangeiro.cidade": [
          {
            required: true,
            message: this.validationMessages.validation.cidade.required,
            trigger: ["blur", "change"]
          }
        ],
        "pessoa.enderecoEstrangeiro.estado": [
          {
            required: true,
            message: this.validationMessages.validation.estado.required,
            trigger: ["blur", "change"]
          }
        ],
        "pessoa.enderecoEstrangeiro.descricaoEndereco": [
          {
            required: true,
            message: this.validationMessages.validation.descricaoEndereco
              .required,
            trigger: ["blur", "change"]
          }
        ],
        senha: [
          { validator: this.validateSenha, trigger: "blur" },
          {
            required: true,
            message: this.validationMessages.validation.senha.required
          },
          {
            min: 6,
            message: this.validationMessages.validation.senha.minLength,
            trigger: "blur"
          }
        ],
        confirmarSenha: [
          { validator: this.validateConfirmacaoSenha, trigger: "blur" },
          {
            required: true,
            message: this.validationMessages.validation.confirmarSenha.required
          }
        ]
      };
    },
    textosSucessoConfirmacao() {
      return this.i18n.cadastro.mensagens.sucessoConfirmacao;
    },
    textoBotaoIrLogin() {
      return this.formatarMensagemModal(
        this.textosSucessoConfirmacao.irLoginComContagem,
        this.segundosRedirecionamentoLogin
      );
    },
    textoRedirecionamento() {
      return this.formatarMensagemModal(
        this.textosSucessoConfirmacao.redirecionamentoEm,
        this.segundosRedirecionamentoLogin
      );
    }
  },
  data() {
    return {
      modalSucessoVisivel: false,
      segundosRedirecionamentoLogin: SEGUNDOS_REDIRECIONAMENTO_LOGIN,
      redirectLoginTimerId: null,
      paises: [],
      form: {
        senha: "",
        confirmarSenha: "",
        pessoa: {
          estrangeiro: false,
          nome: "",
          cpf: "",
          passaporte: "",
          dataNascimentoString: "",
          nomeMae: "",
          sexo: null,
          nacionalidade: null,
          email: "",
          enderecoPrincipal: {
            cep: "",
            logradouro: "",
            numero: "",
            complemento: "",
            bairro: "",
            municipioId: null,
            municipioNome: "",
            uf: "",
            semNumero: false
          },
          enderecoEstrangeiro: {
            zipCode: "",
            pais: null,
            cidade: "",
            estado: "",
            descricaoEndereco: "",
            complemento: ""
          }
        }
      }
    };
  },
  mounted() {
    if (this.$route.query.foreign) {
      this.form.pessoa.estrangeiro = true;
      this.$i18n.locale = "EN";
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
      });
    } else {
      this.$i18n.locale = "PT-BR";
    }

    ConsultaService.fetchPaises()
      .then(({ data }) => {
        this.paises = Array.isArray(data) ? data : [];
      })
      .catch(() => {
        this.$notify.error({
          title: this.i18n.cadastro.mensagens.paisesIndisponiveis.titulo,
          message: this.i18n.cadastro.mensagens.paisesIndisponiveis.mensagem
        });
      });
  },
  beforeDestroy() {
    this.pararRedirecionamentoLogin();
  },
  methods: {
    formatarMensagemModal(template, valor) {
      if (!template) {
        return "";
      }
      return String(template).replace("{0}", String(valor));
    },
    onEstrangeiroChange() {
      this.form.pessoa.enderecoPrincipal = {
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        municipioId: null,
        municipioNome: "",
        uf: "",
        semNumero: false
      };
      this.form.pessoa.enderecoEstrangeiro = {
        zipCode: "",
        pais: null,
        cidade: "",
        estado: "",
        descricaoEndereco: "",
        complemento: ""
      };

      if (!this.form.pessoa.estrangeiro) {
        this.form.pessoa.nacionalidade = null;
      }
      this.$refs.form.clearValidate();

      // Alternar idioma do sistema para refletir nos componentes
      this.$i18n.locale = this.form.pessoa.estrangeiro ? "EN" : "PT-BR";
    },
    validateSenha(rule, value, callback) {
      if (value === "") {
        callback(new Error(this.validationMessages.validation.senha.required));
      } else {
        if (this.form.confirmarSenha !== "") {
          this.$refs.form.validateField("confirmarSenha");
        }
        callback();
      }
    },
    validateConfirmacaoSenha(rule, value, callback) {
      if (value === "") {
        callback(
          new Error(this.validationMessages.validation.confirmarSenha.required)
        );
      } else if (value !== this.form.senha) {
        callback(
          new Error(this.validationMessages.validation.confirmarSenha.match)
        );
      } else {
        callback();
      }
    },
    validateField(fieldName) {
      this.$refs.form.validateField(fieldName);
    },
    aplicarErrosValidacaoServidor(fieldErrors) {
      if (!this.$refs.form || !fieldErrors) {
        return;
      }
      const caminhoParaProp = {
        "pessoa.pessoaFisica.nome": "pessoa.nome",
        "pessoa.pessoaFisica.nomeMae": "pessoa.nomeMae",
        "pessoaFisica.nome": "pessoa.nome",
        "pessoaFisica.nomeMae": "pessoa.nomeMae"
      };
      this.$nextTick(() => {
        const fields = this.$refs.form.fields || [];
        Object.keys(fieldErrors).forEach(caminhoApi => {
          const texto = fieldErrors[caminhoApi];
          if (typeof texto !== "string") {
            return;
          }
          const prop = caminhoParaProp[caminhoApi] || caminhoApi;
          const field = fields.find(f => f.prop === prop);
          if (field) {
            field.validateState = "error";
            field.validateMessage = texto;
          }
        });
      });
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          RegistroService.cadastrarUsuario(
            buildUsuarioRegistroPayload(this.form)
          )
            .then(() => {
              this.modalSucessoVisivel = true;
              this.$nextTick(() => this.iniciarRedirecionamentoLogin());
            })
            .catch(err => {
              console.error(err);
              if (err && err.status === 400 && err.fieldErrors) {
                this.aplicarErrosValidacaoServidor(err.fieldErrors);
              }
              this.$notify(
                buildErrorNotifyPayload(
                  this.$createElement,
                  err,
                  this.i18n.cadastro.mensagens.erro.mensagem
                )
              );
            });
        } else {
          this.$notify.warning({
            title: this.i18n.cadastro.mensagens.atencao.titulo,
            message: this.i18n.cadastro.mensagens.atencao.mensagem
          });
          return false;
        }
      });
    },
    iniciarRedirecionamentoLogin() {
      this.pararRedirecionamentoLogin();
      this.segundosRedirecionamentoLogin = SEGUNDOS_REDIRECIONAMENTO_LOGIN;
      this.redirectLoginTimerId = setInterval(() => {
        if (this.segundosRedirecionamentoLogin <= 1) {
          this.irParaLogin();
          return;
        }
        this.segundosRedirecionamentoLogin -= 1;
      }, 1000);
    },
    pararRedirecionamentoLogin() {
      if (this.redirectLoginTimerId) {
        clearInterval(this.redirectLoginTimerId);
        this.redirectLoginTimerId = null;
      }
    },
    irParaLogin() {
      this.pararRedirecionamentoLogin();
      this.modalSucessoVisivel = false;
      this.$router.push("/login");
    }
  }
};
</script>

<style scoped>
.cadastro-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 0 10px;
}
.title {
  text-align: center;
  margin-bottom: 30px;
  margin-top: 0px;
  font-size: 24px;
}
.box-card {
  padding: 20px;
}
.toggle-tipo {
  text-align: center;
  margin-bottom: 20px;
}
.section-title h3 {
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  margin-bottom: 20px;
  margin-top: 30px;
  color: #295661;
}
.actions {
  text-align: right;
  margin-top: 20px;
}

.modal-sucesso-descricao {
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.5;
  color: #3d4f57;
}

.modal-sucesso-hint {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #295661;
}

@media (max-width: 767px) {
  .cadastro-container {
    padding: 0 8px;
    margin: 8px auto;
  }
  .title {
    font-size: 1.25rem;
    margin-bottom: 16px;
    line-height: 1.3;
  }
  .box-card {
    padding: 12px;
  }
  .toggle-tipo :deep(.el-radio-group) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  .toggle-tipo :deep(.el-radio) {
    margin-right: 0;
  }
  .section-title h3 {
    font-size: 1rem;
    margin-top: 20px;
  }
  .cadastro-usuario__actions {
    display: flex;
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 10px;
    text-align: center;
    margin-top: 24px;
  }
  .cadastro-usuario__actions :deep(.el-button) {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
</style>

<style>
#cadastro-usuario
  .el-form-item.is-required:not(.is-no-asterisk)
  > .el-form-item__label:before,
#cadastro-usuario
  .el-form-item.is-required:not(.is-no-asterisk)
  .el-form-item__label-wrap
  > .el-form-item__label:before {
  content: none !important;
  margin-right: 0 !important;
}

#cadastro-usuario
  .el-form-item.is-required:not(.is-no-asterisk)
  > .el-form-item__label:after,
#cadastro-usuario
  .el-form-item.is-required:not(.is-no-asterisk)
  .el-form-item__label-wrap
  > .el-form-item__label:after {
  content: "*";
  color: #f56c6c;
  margin-left: 4px;
}

@media (max-width: 767px) {
  #cadastro-usuario .el-form--label-top .el-form-item__label {
    width: 100% !important;
    line-height: 1.35;
    padding-bottom: 6px;
  }
  #cadastro-usuario .el-form-item {
    margin-bottom: 18px;
  }
  #cadastro-usuario .el-input__inner,
  #cadastro-usuario .el-textarea__inner {
    font-size: 16px;
    min-height: 44px;
  }
  #cadastro-usuario .el-textarea__inner {
    min-height: 88px;
  }
  #cadastro-usuario .el-select .el-input__inner {
    min-height: 44px;
  }
  #cadastro-usuario .el-date-editor.el-input {
    width: 100% !important;
  }
  #cadastro-usuario .el-date-editor .el-input__inner {
    min-height: 44px;
  }
}
</style>
